import React, { useEffect, useState, Dispatch, useMemo } from 'react';
import { FieldArrayWithId, useFieldArray, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Container, Draggable, OnDropCallback } from 'react-smooth-dnd';

import BaseButton from '@/components/common/Button/BaseButton';
import { ButtonIconButton } from '@/components/common/Button/IconButton';
import { FormContainerDropdown } from '@/components/common/Form/Container/Dropdown';
import { FormContainerTextfield } from '@/components/common/Form/Container/Textfield';
import { FormLayoutFieldset } from '@/components/common/Form/Layout/Fieldset';
import { FormLayoutUnitDivider } from '@/components/common/Form/Layout/UnitDivider';
import { ToggleLabelButton } from '@/components/common/Form/ToggleLabelButton';
import { ModalEditable, ModalEditableBody } from '@/components/common/Modal/Editable';
import { Sorting } from '@/components/common/Form/Unit/Sorting';
import { TitleSectionTitle } from '@/components/common/Title/SectionTitle';
import { DATE_MONTH, DATE_YEAR } from '@/definition/DATE_OPTIONS';
import { PLACEHOLDER } from '@/definition/PLACEHOLDER';
import { TSectionProps } from '@/pages/Resume/Confirmation/Edit';
import { setPrepareFrag } from '@/redux';
import { EducationData } from '@/utils/api-client';

export type TEducation = {
  e_id: string;
  e_graduate_date?: string;
  e_graduate_date_yyyy: string;
  e_graduate_date_mm: string;
  e_school_name: string;
};
export type TFormValues = {
  educations: TEducation[];
  education_check?: boolean;
};
export type TEducationOriginal = {
  e_id: string;
  e_graduate_date: string;
  e_school_name: string;
};

export type TEducationFormValues = {
  educations: EducationData[];
  education_check?: boolean;
};

export const ResumeFormSectionEducations = ({
  id,
  fetchData,
  // newEducation,
  // setNewEducation,
  setUpdateEducations,
  setDelteEducations,
  original,
}: // educationCheck,
// setEducationCheck,
TSectionProps & {
  newEducation: TFormValues | undefined;
  setNewEducation: Dispatch<React.SetStateAction<TFormValues | undefined>>;
  setUpdateEducations: Dispatch<React.SetStateAction<number[]>>;
  setDelteEducations: Dispatch<
    React.SetStateAction<
      {
        e_id: string;
        index: number;
      }[]
    >
  >;
  original?: Set<EducationData>;
  educationCheck: boolean;
  setEducationCheck: Dispatch<React.SetStateAction<boolean>>;
}): React.ReactElement => {
  if (!fetchData || !fetchData.educations) return <></>;

  const [currentEducations, setCurrentEducations] = useState<TEducation[]>([]);
  // const [educations] = useState<Set<EducationData>>(fetchData.educations);
  const [showEducationEditor, setShowEducationEditor] = useState(false);
  const dispatch = useDispatch();
  // const context = useFormContext();
  // const education_check = context.watch('education_check');

  const { control, watch } = useFormContext<TFormValues>();
  const { fields, move, remove, append } = useFieldArray<TFormValues, 'educations', 'id'>({
    control,
    name: 'educations',
    keyName: 'id',
  });
  const watchEducationCheck = watch('education_check');

  const educationDataBase = {
    e_id: '',
    e_graduate_date_yyyy: '',
    e_graduate_date_mm: '',
    e_school_name: '',
  };

  useEffect(() => {
    const subscription = watch((value) => {
      setCurrentEducations(value.educations);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    fields.forEach((el) => {
      if (!el.e_graduate_date_yyyy && el.e_graduate_date) {
        el.e_graduate_date_yyyy = el.e_graduate_date?.split('-')[0];
      }
      if (!el.e_graduate_date_mm && el.e_graduate_date) {
        el.e_graduate_date_mm = el.e_graduate_date?.split('-')[1].replace(/^0+/, '');
      }
    });
  }, [fields]);

  // 学歴データをアップデート
  const updateData = (newValues: Array<EducationData>) => {
    // setCurrentEducations(newValues);
    // newValues.forEach((v, i) => {
    //   const splitDate = v.e_graduate_date ? v.e_graduate_date.split('-') : [undefined, undefined];
    //   update(i, {
    //     e_id: v.e_id,
    //     e_graduate_date_yyyy: splitDate[0] ? Number(splitDate[0]).toString() : '',
    //     e_graduate_date_mm: splitDate[1] ? Number(splitDate[1]).toString() : '',
    //     e_school_name: v.e_school_name,
    //   });
    // });
  };

  // 学歴追加
  const onAddEducation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    append(educationDataBase);
  };

  // 学歴削除
  const onRemove = (e_id: string, index: number) => {
    // 未保存の場合は削除対象にしない
    if (e_id) {
      setUpdateEducations((prevState) => {
        if (!prevState.includes(Number(e_id))) return prevState;
        const newState = prevState.filter((state) => state !== Number(e_id));
        return newState;
      });

      setDelteEducations((prevState) => {
        const newState: { e_id: string; index: number }[] = [
          ...prevState,
          { e_id: e_id, index: index },
        ];
        return newState;
      });
    }
    remove(index);
  };

  // react-smooth-dnd の onDrop で発火
  // このイベントで渡される引数によって元々どのインデックスの要素が消えて、どのインデックスに差し込まれたのか判定
  const onDrop: OnDropCallback = ({ removedIndex, addedIndex }) => {
    if (removedIndex !== null && addedIndex !== null) {
      move(removedIndex, addedIndex);
    }
  };

  // スマホは別窓で並び替えと削除を処理する
  const onShowEducationEditor = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    updateData(currentEducations);
    setShowEducationEditor(true);
  };
  const onHideEducationEditor = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setShowEducationEditor(false);
  };

  const setBeforeUnload = (flag: boolean) => {
    dispatch(setPrepareFrag(flag));
  };

  return (
    <div id={id}>
      <>
        <TitleSectionTitle>
          <div className="flex items-center">
            <span className="mr-4">学歴</span>
          </div>
        </TitleSectionTitle>
        <ToggleLabelButton name="education_check" className="mb-16" />
        {watchEducationCheck ? (
          <Container
            onDrop={onDrop}
            dragHandleSelector=".dragHandle"
            lockAxis="y"
            render={(ref) => {
              return (
                <div ref={ref} className="mb-8 sm:mb-18 mbx-formUnit-education">
                  {fields.map((item, index) => {
                    return (
                      <Draggable key={item.id} className="mbx-formUnit-education-box">
                        <EducationField
                          index={index}
                          item={item}
                          onRemove={onRemove}
                          setBeforeUnload={setBeforeUnload}
                        />
                      </Draggable>
                    );
                  })}
                </div>
              );
            }}
          ></Container>
        ) : (
          <div className="flex flex-wrap gap-16 mb-8 opacity-40 pointer-events-none">
            {original &&
              original.map((item: TEducationOriginal, index: number) => {
                return <EducationOriginal item={item} index={index} key={index + 'original'} />;
              })}
          </div>
        )}
        <BaseButton
          iconName="Add"
          size="s"
          theme="tertiary"
          className={!watchEducationCheck ? 'pointer-events-none' : ''}
          onClick={onAddEducation}
          disabled={!watchEducationCheck}
        >
          学歴を追加
        </BaseButton>
        <span className="ml-8 lg:hidden md:hidden">
          <BaseButton
            iconName="Sortable"
            size="s"
            theme="quaternary"
            className={!watchEducationCheck ? 'pointer-events-none' : ''}
            onClick={onShowEducationEditor}
            disabled={!watchEducationCheck}
          >
            並び替え
          </BaseButton>
        </span>
      </>
      {showEducationEditor && (
        <ModalEditable>
          <ModalEditableBody onClose={onHideEducationEditor}>
            <Container
              onDrop={onDrop}
              dragHandleSelector=".dragHandle"
              render={(ref) => (
                <div ref={ref}>
                  {currentEducations?.map((education, index) => (
                    <Draggable
                      key={education.e_id ? education.e_id + 'Draggable' : index + 'DraggableDummy'}
                    >
                      <Sorting
                        handle="dragHandle"
                        visible={currentEducations.length > 1}
                        onDelete={() => onRemove(education.e_id, index)}
                      >
                        <>
                          {`${education.e_graduate_date_yyyy}年${education.e_graduate_date_mm}月`}
                          {education.e_school_name && (
                            <>
                              <br />
                              {education.e_school_name}
                            </>
                          )}
                        </>
                      </Sorting>
                    </Draggable>
                  ))}
                </div>
              )}
            />
          </ModalEditableBody>
        </ModalEditable>
      )}
    </div>
  );
};

type TFieldProps = {
  index: number;
  item: FieldArrayWithId<TFormValues, 'educations', 'id'>;
  onRemove: (e_id: string, index: number) => void;
  setBeforeUnload(flag: boolean): void;
};

const EducationField = ({
  index,
  item,
  onRemove,
  setBeforeUnload,
}: TFieldProps): React.ReactElement => {
  const { register } = useFormContext();
  return (
    <FormLayoutFieldset>
      <input type="hidden" {...register(`educations.${index}.id` as const)} />
      <div className="flex">
        <div className="flex flex-auto sm:flex-wrap">
          <FormContainerDropdown
            defaultValue={item.e_graduate_date ? item.e_graduate_date.split('-')[0] : undefined}
            name={`educations.${index}.e_graduate_date_yyyy`}
            placeholder={PLACEHOLDER.year}
            className="w-92 mkt_mask_items"
            selectList={DATE_YEAR}
          />
          <FormLayoutUnitDivider className="ml-8">年</FormLayoutUnitDivider>

          <FormContainerDropdown
            defaultValue={
              item.e_graduate_date
                ? item.e_graduate_date.split('-')[1].replace(/^0+/, '')
                : undefined
            }
            name={`educations.${index}.e_graduate_date_mm`}
            placeholder={PLACEHOLDER.month}
            className="w-92 ml-8 mkt_mask_items"
            selectList={DATE_MONTH}
          />
          <FormLayoutUnitDivider className="ml-8">月</FormLayoutUnitDivider>
          <FormContainerTextfield
            defaultValue={`${item.e_school_name ? item.e_school_name : ''}`}
            name={`educations.${index}.e_school_name`}
            placeholder={PLACEHOLDER.e_school_name}
            className="flex-auto ml-8 sm:w-auto sm:ml-0 sm:mt-8 mkt_mask_items"
            onFocus={() => setBeforeUnload(true)}
          />
        </div>
        <div className="sm:hidden">
          <div className="flex">
            <ButtonIconButton
              type="gray"
              iconName="Sortable"
              className="dragHandle"
              focus={false}
            ></ButtonIconButton>
            <ButtonIconButton
              type="gray"
              iconName="Clear"
              focus={false}
              onClick={() => onRemove(item.e_id, index)}
            ></ButtonIconButton>
          </div>
        </div>
      </div>
    </FormLayoutFieldset>
  );
};

type TFieldOriginalProps = {
  index: number;
  item: TEducationOriginal;
};

export const EducationOriginal: React.VFC<TFieldOriginalProps> = ({ index, item }) => {
  const year = useMemo(() => {
    if (item.e_graduate_date) {
      return item.e_graduate_date.split('-')[0];
    } else {
      return undefined;
    }
  }, [item]);

  const month = useMemo(() => {
    if (item.e_graduate_date) {
      return item.e_graduate_date.split('-')[1].replace(/^0+/, '');
    } else {
      return undefined;
    }
  }, [item]);

  return (
    <div className="flex w-full">
      <div className="flex flex-auto sm:flex-wrap">
        <div className="w-92 mkt_mask_items">
          <div className="mbx-formContainer--dropdown-wrapper">
            <div className="mbx-formContainer mbx-formContainer--dropdown"></div>
            <div className="mbx-formContainer--dropdown-arrow">
              <button
                type="button"
                className="hitArea_mini size_default mbx-button-iconButton color-gray hover:text-button-iconButton-gray-hover"
              >
                <i className="mbx-icon mbx-icon-Arrow_Down"></i>
              </button>
            </div>
            <div className="mbx-formContainer--dropdown-multiple-placeholder">
              <p>{year}</p>
            </div>
          </div>
          <div className="flex flex-row"></div>
        </div>
        <FormLayoutUnitDivider className="ml-8">年</FormLayoutUnitDivider>
        <div className="w-92 ml-8 mkt_mask_items">
          <div className="mbx-formContainer--dropdown-wrapper">
            <div className="mbx-formContainer mbx-formContainer--dropdown"></div>
            <div className="mbx-formContainer--dropdown-arrow">
              <div>
                <button
                  type="button"
                  className="hitArea_mini size_default mbx-button-iconButton color-gray hover:text-button-iconButton-gray-hover"
                >
                  <i className="mbx-icon mbx-icon-Arrow_Down"></i>
                </button>
              </div>
            </div>
            <div className="mbx-formContainer--dropdown-multiple-placeholder">
              <p>{month}</p>
            </div>
          </div>
          <div className="flex flex-row"></div>
        </div>
        <FormLayoutUnitDivider className="ml-8">月</FormLayoutUnitDivider>
        <div className="flex-auto ml-8 sm:w-auto sm:ml-0 sm:mt-8 mkt_mask_items">
          <div className="mbx-formContainer--textfield-wrapper">
            <input
              type="text"
              className="mbx-formContainer mbx-formContainer--textfield"
              value={item.e_school_name}
              readOnly
            />
          </div>
        </div>
      </div>
      <div className="sm:hidden">
        <div className="flex">
          <ButtonIconButton
            type="gray"
            iconName="Sortable"
            className="dragHandle"
            focus={false}
          ></ButtonIconButton>
          <ButtonIconButton type="gray" iconName="Clear" focus={false}></ButtonIconButton>
        </div>
      </div>
    </div>
  );
};
